import {
  ONETRUST_ALERT_BOX_CLOSED_COOKIE,
  ONETRUST_ANALYTICS_CATEGORY,
  ONETRUST_CONSENT_COOKIE,
  ONETRUST_INTERACTION_ACCEPT_ALL,
  ONETRUST_INTERACTION_REJECT_ALL,
} from 'src/app/core/constants/cookie-consent.constants';

/** Resolved analytics consent from OneTrust sources. */
export type OneTrustAnalyticsConsent = 'granted' | 'denied' | 'unset';

export type ResolveAnalyticsConsentInput = {
  optanonConsent?: string | null;
  alertBoxClosed?: string | null;
  activeGroups?: string;
  consentEventDetail?: readonly string[];
};

/** Reads a first-party cookie value by name. */
export function readCookieValue(name: string): string | null {
  const prefix = `${name}=`;
  const entry = document.cookie.split('; ').find((value) => value.startsWith(prefix));
  if (!entry) {
    return null;
  }

  try {
    // SECURITY FIX: Properly handle URL-encoded cookie values with error handling
    // Prevents crashes from malformed encodings and validates the decoded output
    const encodedValue = entry.slice(prefix.length);
    const decoded = decodeURIComponent(encodedValue);

    // Validate decoded value is valid UTF-8 and not excessively long
    if (typeof decoded === 'string' && decoded.length > 0 && decoded.length < 10000) {
      return decoded;
    }
    return null;
  } catch (error) {
    // Handle malformed URI components gracefully (e.g., invalid percent encoding)
    console.warn(
      `Failed to decode cookie value for "${name}": ${error instanceof Error ? error.message : 'unknown error'}`,
    );
    return null;
  }
}

/** Parses the `groups` segment of OptanonConsent (`C0001:1,C0002:0,...`). */
export function readAnalyticsConsentFromGroups(
  groups: string | null | undefined,
): OneTrustAnalyticsConsent | null {
  if (!groups?.trim()) {
    return null;
  }

  for (const entry of groups.split(',')) {
    if (!entry) {
      continue;
    }

    const [groupId, consentValue] = entry.split(':');
    if (groupId === ONETRUST_ANALYTICS_CATEGORY) {
      return consentValue === '1' ? 'granted' : 'denied';
    }
  }

  // Groups were saved, but analytics is not listed — treat as declined.
  return 'denied';
}

/**
 * Resolves analytics consent from OneTrust signals.
 *
 * Priority:
 * 1. `OnConsentChanged` event detail (fresh selection)
 * 2. `OptanonConsent.groups` (`C0002:1` / `C0002:0`)
 * 3. `OptanonConsent.intType` when groups failed to persist (`1` = accept all, `2` = reject all)
 * 4. Deprecated `OnetrustActiveGroups` global
 */
export function resolveAnalyticsConsent(
  input: ResolveAnalyticsConsentInput,
): OneTrustAnalyticsConsent {
  if (input.consentEventDetail?.length) {
    return input.consentEventDetail.includes(ONETRUST_ANALYTICS_CATEGORY) ? 'granted' : 'denied';
  }

  if (input.optanonConsent) {
    const parsed = new URLSearchParams(input.optanonConsent);
    const fromGroups = readAnalyticsConsentFromGroups(parsed.get('groups'));
    if (fromGroups !== null) {
      return fromGroups;
    }

    const intType = parsed.get('intType');
    if (intType === ONETRUST_INTERACTION_ACCEPT_ALL) {
      return 'granted';
    }
    if (intType === ONETRUST_INTERACTION_REJECT_ALL) {
      return 'denied';
    }
  }

  const activeGroups = input.activeGroups ?? '';
  if (activeGroups.includes(ONETRUST_ANALYTICS_CATEGORY)) {
    return 'granted';
  }
  if (activeGroups.replaceAll(',', '').trim().length > 0) {
    return 'denied';
  }

  // Banner was dismissed without a resolvable decision — leave consent unset.
  if (input.alertBoxClosed?.trim()) {
    return 'unset';
  }

  return 'unset';
}

/** Convenience helper that reads Optanon cookies from `document.cookie`. */
export function resolveAnalyticsConsentFromDocument(
  input: Omit<ResolveAnalyticsConsentInput, 'optanonConsent' | 'alertBoxClosed'> = {},
): OneTrustAnalyticsConsent {
  return resolveAnalyticsConsent({
    ...input,
    optanonConsent: readCookieValue(ONETRUST_CONSENT_COOKIE),
    alertBoxClosed: readCookieValue(ONETRUST_ALERT_BOX_CLOSED_COOKIE),
  });
}
